import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  InformationCircleIcon,
  UserGroupIcon,
  NewspaperIcon,
  TrophyIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  UsersIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ChartBarIcon,
  CogIcon,
  MapPinIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  QuestionMarkCircleIcon,
  DevicePhoneMobileIcon,
  FilmIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import LoginModal from '../../components/LoginModal';
import { toast } from 'react-hot-toast';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import type { AdminTab } from '../../lib/rolePermissions';

interface MenuSection {
  title: string;
  items: MenuItem[];
}

interface MenuItem {
  label: string;
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  tab: AdminTab;
}

const PUBLIC_MENU: MenuSection[] = [
  {
    title: 'Tournament',
    items: [
      { label: 'About Tournament', path: '/m/about', icon: InformationCircleIcon, color: 'bg-blue-50 text-blue-600' },
      { label: 'Promo', path: '/m/promo', icon: FilmIcon, color: 'bg-amber-50 text-amber-600' },
      { label: 'Prize Breakdown', path: '/m/prize-breakdown', icon: CurrencyDollarIcon, color: 'bg-green-50 text-green-600' },
      { label: 'Registration', path: '/m/registration', icon: ClipboardDocumentListIcon, color: 'bg-golf-orange/10 text-golf-orange' },
    ]
  },
  {
    title: 'Connect',
    items: [
      { label: 'Partners & Sponsors', path: '/m/sponsors', icon: UserGroupIcon, color: 'bg-rose-50 text-rose-600' },
      { label: 'Press', path: '/m/press', icon: NewspaperIcon, color: 'bg-slate-50 text-slate-600' },
    ]
  },
  {
    title: 'Help',
    items: [
      { label: 'Support', path: '/m/support', icon: QuestionMarkCircleIcon, color: 'bg-blue-50 text-blue-600' },
    ]
  },
  {
    title: 'Legal',
    items: [
      { label: 'Privacy Policy', path: '/m/privacy-policy', icon: ShieldCheckIcon, color: 'bg-gray-50 text-gray-500' },
      { label: 'Terms of Service', path: '/m/terms', icon: DocumentTextIcon, color: 'bg-gray-50 text-gray-500' },
    ]
  }
];

const ADMIN_ITEMS: MenuItem[] = [
  { label: 'Dashboard', path: '/m/admin', icon: Squares2X2Icon, color: 'bg-golf-orange/10 text-golf-orange', tab: 'dashboard' },
  { label: 'Tournaments', path: '/m/admin/tournaments', icon: CogIcon, color: 'bg-amber-50 text-amber-600', tab: 'tournaments' },
  { label: 'Players', path: '/m/admin/players', icon: UsersIcon, color: 'bg-blue-50 text-blue-600', tab: 'players' },
  { label: 'Scores', path: '/m/scorer/groups', icon: ChartBarIcon, color: 'bg-golf-orange/10 text-golf-orange', tab: 'scores' },
  { label: 'Partners', path: '/m/admin/partners', icon: BuildingOfficeIcon, color: 'bg-teal-50 text-teal-600', tab: 'partners' },
  { label: 'Schedule', path: '/m/admin/calendar', icon: CalendarIcon, color: 'bg-blue-50 text-blue-600', tab: 'calendar' },
  { label: 'Finance', path: '/m/admin/finance', icon: ChartBarIcon, color: 'bg-green-50 text-green-600', tab: 'finance' },
  { label: 'Analytics', path: '/m/admin/analytics', icon: ChartBarIcon, color: 'bg-slate-50 text-slate-600', tab: 'analytics' },
  { label: 'Press', path: '/m/admin/press', icon: NewspaperIcon, color: 'bg-slate-50 text-slate-600', tab: 'press' },
  { label: 'Content', path: '/m/admin/content', icon: DocumentTextIcon, color: 'bg-blue-50 text-blue-600', tab: 'contents' },
  { label: 'Messaging', path: '/m/admin/messaging', icon: ChatBubbleLeftRightIcon, color: 'bg-sky-50 text-sky-600', tab: 'messaging' },
  { label: 'Courses', path: '/m/admin/courses', icon: MapPinIcon, color: 'bg-green-50 text-green-600', tab: 'courses' },
  { label: 'Settings', path: '/m/admin/settings', icon: CogIcon, color: 'bg-gray-50 text-gray-500', tab: 'settings' },
  { label: 'My Account', path: '/m/admin/settings/account', icon: UserCircleIcon, color: 'bg-red-50 text-red-500', tab: 'account' },
];

const PAGE_PUBLISH_KEYS: Record<string, string> = {
  '/m/press': 'press_published',
  '/m/schedule': 'schedule_published',
  '/m/leaderboard': 'leaderboard_published',
};

export default function MobileMore() {
  const navigate = useNavigate();
  const { user: currentUser, loading: userLoading } = useCurrentUser();
  const [loginOpen, setLoginOpen] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [publishStatus, setPublishStatus] = useState<Record<string, boolean>>({});
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    const keys = Object.values(PAGE_PUBLISH_KEYS);
    supabase
      .from('system_settings')
      .select('key, value')
      .in('key', keys)
      .then(({ data }) => {
        const status: Record<string, boolean> = {};
        keys.forEach(k => { status[k] = true; });
        (data ?? []).forEach(row => {
          status[row.key] = row.value === true || row.value === 'true';
        });
        setPublishStatus(status);
      });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('device_push_token');
    if (storedToken) {
      setDeviceToken(storedToken);
      return;
    }
    const fetchDeviceToken = async () => {
      const { data } = await supabase
        .from('device_tokens')
        .select('token, platform')
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) {
        setDeviceToken(data.token);
        localStorage.setItem('device_push_token', data.token);
        localStorage.setItem('device_token_registered', 'true');
      }
    };
    fetchDeviceToken();
  }, []);

  const isPublished = (path: string): boolean => {
    const key = PAGE_PUBLISH_KEYS[path];
    if (!key) return true;
    return publishStatus[key] !== false;
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      toast.success('Signed out');
      navigate('/m');
    } catch {
      toast.error('Failed to sign out');
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="px-4 pt-2 pb-10">
      {/* Banner */}
      <div
        className="relative rounded-2xl overflow-hidden mb-6 h-28 bg-cover bg-center"
        style={{ backgroundImage: `url(https://images.pexels.com/photos/1325681/pexels-photo-1325681.jpeg?auto=compress&w=800)` }}
      >
        <div className="absolute inset-0 bg-golf-green/80" />
        <div className="relative z-10 p-4 h-full flex flex-col justify-center">
          <p className="text-golf-sand text-xs font-medium uppercase tracking-wide">Nigeria's Premier</p>
          <p className="text-white font-display font-bold text-xl">Ibom Open</p>
          <p className="text-golf-sand text-xs mt-0.5">Ibom Golf &amp; Country Club, Uyo</p>
        </div>
      </div>

      {/* Admin section (logged in) */}
      {session && !userLoading && (() => {
        const allowedTabs = currentUser?.allowedTabs ?? [];
        const visibleAdminItems = ADMIN_ITEMS.filter(item => allowedTabs.includes(item.tab));
        if (visibleAdminItems.length === 0) return null;
        return (
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">Admin</p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {visibleAdminItems.map((item, i) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3.5 active:bg-gray-50 transition-colors ${i < visibleAdminItems.length - 1 ? 'border-b border-gray-50' : ''}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <span className="flex-1 font-medium text-gray-700 text-sm">{item.label}</span>
                  <ChevronRightIcon className="w-4 h-4 text-gray-300" />
                </Link>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Public menu */}
      {PUBLIC_MENU.map((section) => {
        const visibleItems = section.items.filter(item => isPublished(item.path));
        if (visibleItems.length === 0) return null;
        return (
          <div key={section.title} className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">{section.title}</p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {visibleItems.map((item, i) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3.5 active:bg-gray-50 transition-colors ${i < visibleItems.length - 1 ? 'border-b border-gray-50' : ''}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <span className="flex-1 font-medium text-gray-700 text-sm">{item.label}</span>
                  <ChevronRightIcon className="w-4 h-4 text-gray-300" />
                </Link>
              ))}
            </div>
          </div>
        );
      })}

      {/* Login / Logout */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {session ? (
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-gray-50 transition-colors"
          >
            <div className="w-9 h-9 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0">
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </div>
            <span className="flex-1 font-medium text-gray-700 text-sm text-left">
              {signingOut ? 'Signing out...' : 'Sign Out'}
            </span>
          </button>
        ) : (
          <button
            onClick={() => setLoginOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-gray-50 transition-colors"
          >
            <div className="w-9 h-9 rounded-xl bg-gray-50 text-gray-500 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </div>
            <span className="flex-1 font-medium text-gray-700 text-sm text-left">Admin Login</span>
            <ChevronRightIcon className="w-4 h-4 text-gray-300" />
          </button>
        )}
      </div>

      <p className="text-center text-xs text-gray-300 mt-8">&copy; 2004 &ndash; {new Date().getFullYear()} Ibom Open. All rights reserved.</p>

      {/* Device Token */}
      <div className="mt-6 mx-2">
        <div className="flex items-center gap-2 mb-1.5">
          <DevicePhoneMobileIcon className="w-3.5 h-3.5 text-gray-300" />
          <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Device Token</span>
        </div>
        {deviceToken ? (
          <button
            onClick={() => {
              navigator.clipboard.writeText(deviceToken);
              setTokenCopied(true);
              setTimeout(() => setTokenCopied(false), 2000);
            }}
            className="w-full text-left bg-gray-50 rounded-lg px-3 py-2 border border-gray-100"
          >
            <p className="text-[10px] text-gray-400 font-mono break-all leading-relaxed">{deviceToken}</p>
            <p className="text-[9px] text-gray-300 mt-1">{tokenCopied ? 'Copied!' : 'Tap to copy'}</p>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-gray-300 italic">No device token registered</p>
            <button
              onClick={() => {
                if (window.requestPushToken) {
                  window.requestPushToken();
                  toast.success('Requesting token from app...');
                } else {
                  toast.error('Push notifications require the native app');
                }
              }}
              disabled={registering}
              className="text-[10px] font-medium text-golf-green bg-golf-green/10 px-2.5 py-1 rounded-md hover:bg-golf-green/20 transition-colors disabled:opacity-50"
            >
              Register
            </button>
          </div>
        )}
      </div>

      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} mobileRedirect="/m/admin" />
    </div>
  );
}
